import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronRight, Home, MapPin } from "lucide-react";
import { Badge, Card, TruncatedText } from "@/components/ui";
import type { House } from "@/types";
import { commissionPercentStringFromRate } from "@/lib/commissionRate";

interface HouseCardProps {
  house: House;
  showCommission: boolean;
}

export function HouseCard({ house, showCommission }: HouseCardProps) {
  const { t } = useTranslation("houses");

  return (
    <Link to={`/houses/${house.id}`} className="block h-full">
      <Card className="group flex h-full flex-col overflow-hidden transition-[box-shadow,border-color,transform] duration-200 animate-slide-up hover:-translate-y-px hover:border-surface-300 hover:shadow-elevated">
        <div className="flex flex-1 items-start gap-3.5 px-5 py-4">
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600 ring-1 ring-inset ring-primary-600/15 transition-colors group-hover:bg-primary-100"
            aria-hidden
          >
            <Home className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 text-[15px] font-semibold tracking-tight text-surface-900">
            {house.name}
          </h3>
          {house.address ? (
            <p className="mt-1.5 flex items-start gap-1.5 text-[13px] text-surface-500">
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-surface-400" />
              <span className="line-clamp-2">{house.address}</span>
            </p>
          ) : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-surface-200/70 bg-surface-50/60 px-5 py-2.5">
          <div className="flex min-w-0 items-center gap-2">
            <Badge
              variant={house.is_active ? "success" : "default"}
              size="sm"
              dot
            >
              {house.is_active ? t("list.active") : t("list.inactive")}
            </Badge>
            {showCommission ? (
              <TruncatedText className="text-xs text-surface-500">
                {t("list.commissionRate", {
                  percent: commissionPercentStringFromRate(
                    house.commission_rate,
                  ),
                })}
              </TruncatedText>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1 text-[13px] text-surface-500 transition-colors group-hover:text-primary-700">
            <span className="font-medium">{t("list.openHouse")}</span>
            <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </Card>
    </Link>
  );
}
